// Speech synthesis for card audio. The browser's built-in voices cost nothing
// and work offline — the trade is that quality varies enormously by device, so
// the default picks the best available and Settings lets you override it.
//
// Voice quality by platform, roughly:
//   iOS / macOS  good; "Siri" and "Enhanced" voices are better again
//   Android      good; the Google voices are the ones to want
//   Windows      poor by default. The legacy SAPI voices (Hortense, Helena)
//                are what you get unless a neural voice is present. Edge ships
//                "Microsoft ... Online (Natural)" voices, which are far better
//                but are network-backed, so a naive preference for local
//                voices actively selects the worst option available.

import { LANG_LOCALES, type Lang } from "./types";

let voices: SpeechSynthesisVoice[] = [];
let loaded = false;
const listeners = new Set<() => void>();

function readVoices(): SpeechSynthesisVoice[] {
  if (typeof speechSynthesis === "undefined") return [];
  return speechSynthesis.getVoices();
}

/**
 * Re-read the voice list and notify subscribers, but only when it actually
 * changed. Notifying unconditionally recurses: a subscriber that calls
 * listVoices would refresh again, which would notify again — and an empty
 * voice list, which is the normal state on a machine with no language pack
 * installed, makes that loop immediate.
 */
function refreshVoices() {
  const next = readVoices();
  const changed =
    next.length !== voices.length ||
    next.some((voice, i) => voice.voiceURI !== voices[i]?.voiceURI);
  voices = next;
  loaded = true;
  if (changed) for (const listener of listeners) listener();
}

/** The cached list, populated on first use. Never notifies. */
function currentVoices(): SpeechSynthesisVoice[] {
  if (!loaded) {
    voices = readVoices();
    loaded = true;
  }
  return voices;
}

if (typeof speechSynthesis !== "undefined") {
  currentVoices();
  // Voices load asynchronously on most browsers, and on iOS not until the list
  // is first requested after a user gesture.
  speechSynthesis.addEventListener("voiceschanged", refreshVoices);
}

/** Notify when the voice list arrives, so a picker can re-render. */
export function onVoicesChanged(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function isSupported(): boolean {
  return typeof speechSynthesis !== "undefined";
}

function normaliseLang(value: string): string {
  return value.replace("_", "-").toLowerCase();
}

/**
 * Rank a voice for a language. Negative means unusable.
 *
 * The naming conventions do the work: the good Windows voices announce
 * themselves as "Online (Natural)", Apple's better ones as "Enhanced" or
 * "Siri", and Android's as "Google". Scoring on those keywords separates the
 * neural voices from the legacy ones without a brittle list of bad names.
 */
function score(voice: SpeechSynthesisVoice, lang: Lang): number {
  const locale = normaliseLang(LANG_LOCALES[lang]);
  const prefix = locale.slice(0, 2);
  const voiceLang = normaliseLang(voice.lang);

  let total: number;
  if (voiceLang === locale) total = 10;
  else if (voiceLang.startsWith(prefix)) total = 2;
  else return -1;

  const name = voice.name.toLowerCase();
  if (/natural|neural|online/.test(name)) total += 40;
  if (/premium|enhanced/.test(name)) total += 30;
  if (/siri/.test(name)) total += 25;
  if (/google/.test(name)) total += 20;
  // A small nudge only: being offline-capable is worth something, but never
  // enough to outrank a markedly better-sounding network voice.
  if (voice.localService) total += 3;
  return total;
}

/** Every usable voice for a language, best first. */
export function listVoices(lang: Lang): SpeechSynthesisVoice[] {
  return currentVoices()
    .map((voice) => ({ voice, rank: score(voice, lang) }))
    .filter((entry) => entry.rank >= 0)
    .sort((a, b) => b.rank - a.rank)
    .map((entry) => entry.voice);
}

/** The chosen voice if it is still installed, otherwise the best available. */
export function resolveVoice(lang: Lang, voiceURI?: string | null): SpeechSynthesisVoice | undefined {
  const candidates = listVoices(lang);
  if (voiceURI) {
    const chosen = candidates.find((v) => v.voiceURI === voiceURI);
    if (chosen) return chosen;
  }
  return candidates[0];
}

export interface SpeakOptions {
  voiceURI?: string | null;
  rate?: number;
}

export function speak(text: string, lang: Lang, options: SpeakOptions = {}): void {
  if (!isSupported() || !text.trim()) return;
  speechSynthesis.cancel();
  const utterance = new SpeechSynthesisUtterance(text);
  utterance.lang = LANG_LOCALES[lang];
  const voice = resolveVoice(lang, options.voiceURI);
  if (voice) utterance.voice = voice;
  utterance.rate = options.rate ?? 0.95;
  speechSynthesis.speak(utterance);
}

export function stop(): void {
  if (isSupported()) speechSynthesis.cancel();
}

/** True when a usable voice exists, so the UI can hide the speaker button. */
export function hasVoice(lang: Lang): boolean {
  return isSupported() && listVoices(lang).length > 0;
}

/** A sentence to audition a voice with, long enough to judge the prosody. */
export const SAMPLE: Record<Lang, string> = {
  fr: "Le gisement fera l'objet d'une consultation publique au printemps.",
  es: "La mina está cerrada desde marzo, sin embargo el recorrido continúa.",
};
